import * as Sentry from '@sentry/react';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import type { z } from 'zod';
import axiosInstance, { axiosInstanceWithoutSuffix } from '@/api/utils/axiosInstance';
import { environment } from '@/utils/env.utils';

type BaseOptions = AxiosRequestConfig & {
  url: NonNullable<AxiosRequestConfig['url']>;
  method: NonNullable<AxiosRequestConfig['method']>;
};

type ValidatedOptions<T extends z.ZodTypeAny> = BaseOptions & { schema: T };
type UnvalidatedOptions = BaseOptions & { schema?: undefined };
type AnyOptions = ValidatedOptions<z.ZodTypeAny> | UnvalidatedOptions;

const isValidatedOptions = <T extends z.ZodTypeAny>(
  options: ValidatedOptions<T> | UnvalidatedOptions,
): options is ValidatedOptions<T> => options.schema !== undefined && 'safeParse' in options.schema;

/** Strip dynamic IDs so e.g. `/payment-provider/direct-payment/81982C8E97FF49` → `…/direct-payment/:id`. */
export const normalizeApiPath = (url: string): string =>
  url
    .split('?')[0]
    .replaceAll(
      /\/(?:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}|[0-9a-fA-F]{8,}|\d+)(?=\/|$)/g,
      '/:id',
    );

const validateAndReport = <T extends z.ZodTypeAny>(
  schema: T,
  data: unknown,
  url: string,
  method: string,
): z.infer<T> => {
  const result = schema.safeParse(data);

  if (!result.success) {
    if (environment.env === 'production') {
      Sentry.captureException(result.error, scope => {
        scope.setFingerprint(['zod-api-validation', `${method} ${normalizeApiPath(url)}`]);
        scope.addAttachment({
          filename: 'response-data.json',
          data: JSON.stringify(data, null, 2),
          contentType: 'application/json',
        });
        return scope;
      });
    } else {
      // eslint-disable-next-line no-console
      console.error(result.error);
    }
  }

  return data as z.infer<T>;
};

type RequestFn = {
  <T extends z.ZodTypeAny>(options: ValidatedOptions<T>): Promise<z.infer<T>>;
  <T = unknown>(options: UnvalidatedOptions): Promise<T>;
};

const createApi =
  (instance: AxiosInstance): RequestFn =>
  async (options: AnyOptions) => {
    const { schema: _schema, url, method, ...rest } = options;
    const response = await instance.request({ url, method, ...rest });
    if (isValidatedOptions(options)) {
      return validateAndReport(options.schema, response.data, url, method);
    }
    return response.data as never;
  };

export const apiRequest = createApi(axiosInstance);
export const apiRequestWithoutSuffix = createApi(axiosInstanceWithoutSuffix);
export const apiBaseURL = axiosInstance.defaults.baseURL;
